'use client';

import PopupResponsive from '@/components/PopupResponsive';
import { toast } from '@/components/Toast';
import { awsUpload } from '@/utils/aws-s3';
import { httpRequest } from '@/utils/axios';
import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteCircleFilled, PlusOutlined, ReturnOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useCallback, useRef, useState } from 'react';

interface WritersApplyDialogProps {
  showDialog?: boolean;
  onClose?: () => void;
}

interface FormData {
  name: string;
  email: string;
  sample: Array<{
    path: string;
    name: string;
    type: string;
    uid: string;
  }>;
  location: string;
  linkedln_profile: string;
  portfolio: string;
  content: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  sample?: string;
  location?: string;
  content?: string;
}

const ACCEPTED_FILE_TYPES = [
  '.docx',
  '.pdf',
  '.txt',
  '.ppt',
  '.pptx',
  '.md',
  '.rtf',
  '.jpg',
  '.jpeg',
  '.png',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILE_COUNT = 3;

export default function WritersApplyDialog({
  showDialog = false,
  onClose,
}: WritersApplyDialogProps) {
  const t = useTranslations('write-for-us.dialog');
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    sample: [],
    location: '',
    linkedln_profile: '',
    portfolio: '',
    content: '',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // TODO: 需要实现文件上传到本地状态的映射
  const formRef = useRef<HTMLFormElement>(null);

  // 重置表单
  const resetForm = useCallback(() => {
    setForm({
      name: '',
      email: '',
      sample: [],
      location: '',
      linkedln_profile: '',
      portfolio: '',
      content: '',
    });
    setErrors({});
  }, []);

  // 表单验证
  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    if (!form.name.trim()) {
      newErrors.name = t('pleaseEnterYourName');
    }

    if (!form.email.trim()) {
      newErrors.email = t('pleaseEnterYourEmail');
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      newErrors.email = t('pleaseEnterAValidEmailAddress');
    }

    if (form.sample.length === 0) {
      newErrors.sample = t('pleaseUploadYourWritingSample');
    }

    if (!form.location.trim()) {
      newErrors.location = t('pleaseEnterYourLocation');
    }

    if (!form.content.trim()) {
      newErrors.content = t('pleaseEnterYourReason');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, t]);

  // 限制不允许输入中文
  const preventChinese = useCallback((value: string, field: keyof FormData) => {
    const containsChinese = /[\u4E00-\u9FA5]/.test(value);
    if (!containsChinese) {
      const filteredValue = value.replace(/[\u4E00-\u9FA5]/g, '');
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
    }
  }, []);

  // 处理表单输入变化
  const handleInputChange = useCallback(
    (field: keyof FormData) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;

        if (field === 'content') {
          preventChinese(value, field);
        } else {
          setForm((prev) => ({ ...prev, [field]: value }));
        }

        // 清除对应字段的错误
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    [errors, preventChinese],
  );

  // 验证文件
  const validateFile = useCallback(
    (file: File): boolean => {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (!ACCEPTED_FILE_TYPES.includes(fileExtension)) {
        toast.error(t('supportedFileTypesAndSize'));
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('supportedFileTypesAndSize'));
        return false;
      }

      return true;
    },
    [t],
  );

  // 处理文件上传
  const handleFileUpload = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      if (form.sample.length + fileArray.length > MAX_FILE_COUNT) {
        toast.error(t('youCanOnlyUploadFilesAtATime', { count: MAX_FILE_COUNT }));
        return;
      }

      setUploading(true);

      try {
        for (const file of fileArray) {
          if (validateFile(file)) {
            const fileType = file.type || file.name.split('.').pop();

            try {
              const uploadResult = await awsUpload(2, file, () => {
                // 可以在这里处理上传进度
              });

              const newFile = {
                path: uploadResult as string,
                name: file.name,
                type: fileType,
                uid: Date.now().toString() + Math.random().toString(),
              };

              setForm((prev) => ({
                ...prev,
                sample: [...prev.sample, newFile],
              }));

              // 清除sample字段错误
              if (errors.sample) {
                setErrors((prev) => ({ ...prev, sample: undefined }));
              }
            } catch {
              toast.error(t('uploadFailedPleaseTryAgain'));
            }
          }
        }
      } finally {
        setUploading(false);
      }
    },
    [form.sample.length, validateFile, errors.sample, t],
  );

  // 删除文件
  const handleRemoveFile = useCallback((uid: string) => {
    setForm((prev) => ({
      ...prev,
      sample: prev.sample.filter((file) => file.uid !== uid),
    }));
  }, []);

  // 文件拖拽处理
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files);
      }
    },
    [handleFileUpload],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // 点击上传
  const handleClickUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPTED_FILE_TYPES.join(',');
    input.multiple = true;

    input.onchange = (e) => {
      const { files } = e.target as HTMLInputElement;
      if (files && files.length > 0) {
        handleFileUpload(files);
      }
    };

    input.click();
  }, [handleFileUpload]);

  // 提交表单
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const submitData = {
      ...form,
      sample: form.sample.map((item) => ({
        path: item.path,
        name: `${item.name.split('.')[0]}_${form.name}.${item.name.split('.')[1]}`,
        type: item.type,
      })),
    };

    try {
      const response = await httpRequest('POST:/api/write_for_us/create', {
        data: submitData,
      });

      if (response.code === 200) {
        toast.success(response.msg || t('applicationSubmittedSuccessfully'));
        onClose?.();
        resetForm();
      } else {
        toast.error(response.msg || t('submissionFailed'));
      }
    } catch {
      toast.error(t('submissionFailed'));
    } finally {
      setLoading(false);
    }
  }, [form, validateForm, onClose, resetForm, t]);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    resetForm();
    onClose?.();
  }, [resetForm, onClose]);

  // 监听弹窗显示状态，重置表单
  React.useEffect(() => {
    if (showDialog) {
      resetForm();
    }
  }, [showDialog, resetForm]);

  return (
    <PopupResponsive
      open={showDialog}
      onClose={handleClose}
      title={t('title')}
      position="bottom"
      height="80vh"
      round
      borderRadius={16}
      onOk={handleSubmit}
      okText={t('submitApplication')}
      okDisabled={loading || uploading}
      loading={loading}
      showDivider
      hasFooter
      sx={{
        '& .MuiDialog-paper': {
          width: '680px',
          maxWidth: '680px',
        },
        '& .MuiDialogContent-root': {
          maxHeight: '70vh',
        },
      }}
    >
      <Box
        sx={{
          p: '0',
        }}
      >
        <Box component="form" ref={formRef}>
          {/* 姓名和邮箱 - PC端并排，移动端垂直 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: '24px', md: '16px' },
              mb: { xs: '24px', md: '18px' },
            }}
          >
            <FormControl fullWidth sx={{ flex: { xs: 'none', md: 1 } }}>
              <FormLabel
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  color: '#282828',
                  fontFamily: 'var(--font-bricolage-bold)',
                  mb: { xs: '12px', md: '0' },
                }}
              >
                {t('fullName')}*
              </FormLabel>
              <TextField
                value={form.name}
                onChange={handleInputChange('name')}
                placeholder={t('fullNamePlaceholder')}
                inputProps={{ maxLength: 150 }}
                error={!!errors.name}
                helperText={errors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                    borderRadius: '6px',
                    height: { xs: '46px', md: 'auto' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#A9A9A9',
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ flex: { xs: 'none', md: 1 } }}>
              <FormLabel
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  color: '#282828',
                  fontFamily: 'var(--font-bricolage-bold)',
                  mb: { xs: '12px', md: '0' },
                }}
              >
                {t('email')}*
              </FormLabel>
              <TextField
                value={form.email}
                onChange={handleInputChange('email')}
                placeholder={t('emailPlaceholder')}
                type="email"
                inputProps={{ maxLength: 150 }}
                error={!!errors.email}
                helperText={errors.email}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                    borderRadius: '6px',
                    height: { xs: '46px', md: 'auto' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#A9A9A9',
                  },
                }}
              />
            </FormControl>
          </Box>

          {/* 文件上传 */}
          <Box sx={{ mb: '24px' }}>
            <FormLabel
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
                display: 'block',
                mb: { xs: '8px', md: '5px' },
              }}
            >
              {t('writingSample')}*
            </FormLabel>
            <Typography
              sx={{
                fontSize: { xs: '12px', md: '14px' },
                color: '#808080',
                lineHeight: { xs: '18px', md: '17px' },
                mb: { xs: '12px', md: '16px' },
              }}
            >
              {t('writingSampleDescription')}
            </Typography>

            {/* 上传区域 */}
            {form.sample.length < MAX_FILE_COUNT && (
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleClickUpload}
                sx={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '6px',
                  padding: { xs: '18px 12px', md: '20px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  height: { xs: '90px', md: 'auto' },
                  mb: form.sample.length > 0 ? '8px' : 0,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                {uploading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(244, 244, 244, 0.8)',
                      borderRadius: '6px',
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                <PlusOutlined className="text-[20px]" />
                <Typography
                  sx={{
                    fontSize: { xs: '11px', md: '12px' },
                    color: '#808080',
                    lineHeight: { xs: '13px', md: '14px' },
                    textAlign: 'center',
                    maxWidth: { xs: 'none', md: '354px' },
                    marginTop: { xs: '0', md: '12px' },
                  }}
                >
                  {t('dragAndDropYourFileHere')}
                </Typography>
              </Box>
            )}

            {/* 文件列表 */}
            {form.sample.length > 0 && (
              <Box sx={{ mt: '8px' }}>
                {form.sample.map((file, index) => (
                  <Box
                    key={file.uid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f4f4f4',
                      borderRadius: '7px',
                      padding: '10px 12px',
                      mb: index < form.sample.length - 1 ? '8px' : 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <ReturnOutlined />
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#282828',
                          lineHeight: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                    <DeleteCircleFilled onClick={() => handleRemoveFile(file.uid)} />
                  </Box>
                ))}
              </Box>
            )}

            {errors.sample && (
              <Typography color="error" sx={{ fontSize: '12px', mt: '4px' }}>
                {errors.sample}
              </Typography>
            )}
          </Box>

          {/* 位置 */}
          <FormControl fullWidth sx={{ mb: { xs: '24px', md: '30px' } }}>
            <FormLabel
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
                mb: { xs: '12px', md: '0' },
              }}
            >
              {t('location')}*
            </FormLabel>
            <TextField
              value={form.location}
              onChange={handleInputChange('location')}
              placeholder={t('locationPlaceholder')}
              inputProps={{ maxLength: 500 }}
              error={!!errors.location}
              helperText={errors.location}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                  borderRadius: '6px',
                  height: { xs: '46px', md: 'auto' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#A9A9A9',
                },
              }}
            />
          </FormControl>

          {/* LinkedIn Profile */}
          <FormControl fullWidth sx={{ mb: { xs: '24px', md: '30px' } }}>
            <FormLabel
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
                mb: { xs: '12px', md: '0' },
              }}
            >
              {t('linkedinProfile')}
            </FormLabel>
            <TextField
              value={form.linkedln_profile}
              onChange={handleInputChange('linkedln_profile')}
              placeholder={t('linkedinProfilePlaceholder')}
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                  borderRadius: '6px',
                  height: { xs: '46px', md: 'auto' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#A9A9A9',
                },
              }}
            />
          </FormControl>

          {/* Portfolio */}
          <FormControl fullWidth sx={{ mb: { xs: '24px', md: '30px' } }}>
            <FormLabel
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
                mb: { xs: '12px', md: '0' },
              }}
            >
              {t('portfolio')}
            </FormLabel>
            <TextField
              value={form.portfolio}
              onChange={handleInputChange('portfolio')}
              placeholder={t('portfolioPlaceholder')}
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                  borderRadius: '6px',
                  height: { xs: '46px', md: 'auto' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#A9A9A9',
                },
              }}
            />
          </FormControl>

          {/* 申请原因 */}
          <FormControl fullWidth sx={{ mb: '24px' }}>
            <FormLabel
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
                mb: { xs: '12px', md: '0' },
              }}
            >
              {t('whyDoYouWantToWriteForUs')}*
            </FormLabel>
            <TextField
              value={form.content}
              onChange={handleInputChange('content')}
              placeholder={t('whyDoYouWantToWriteForUsPlaceholder')}
              multiline
              rows={4}
              inputProps={{ maxLength: 1000 }}
              error={!!errors.content}
              helperText={errors.content || `${form.content.length}/1000`}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: { xs: '#f4f4f4', md: '#FFFFFF' },
                  borderRadius: '6px',
                  height: 'auto',
                },
                '& textarea': {
                  padding: '0',
                },
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '2px',
                  right: '0',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#A9A9A9',
                },
              }}
            />
          </FormControl>
        </Box>
      </Box>
    </PopupResponsive>
  );
}
